import React from "react";
import {
  StrategyConfigDraft,
  RealtimeConfigDraft,
  VADConfigDraft,
  DEFAULT_REALTIME_CONFIG,
  DEFAULT_VAD_CONFIG,
  cloneStrategyConfig,
} from "../../../types/strategyConfig";
import { Strategy } from "../../../types/strategies";

type ConfigWithRealtime = Exclude<StrategyConfigDraft, null>;

interface RealtimeControlsCardProps {
  strategy: Strategy | null;
  strategyConfig: StrategyConfigDraft;
  onStrategyConfigChange: (config: StrategyConfigDraft) => void;
}

const hasRealtime = (
  config: StrategyConfigDraft
): config is ConfigWithRealtime =>
  Boolean(config && typeof (config as ConfigWithRealtime).realtime !== "undefined");

const RealtimeControlsCard: React.FC<RealtimeControlsCardProps> = ({
  strategy,
  strategyConfig,
  onStrategyConfigChange,
}) => {
  if (!strategy || !strategyConfig || !hasRealtime(strategyConfig)) {
    return null;
  }

  const updateRealtime = (mutator: (draft: RealtimeConfigDraft) => void) => {
    const base = cloneStrategyConfig(strategyConfig) as ConfigWithRealtime;
    const nextRealtime = base.realtime
      ? cloneStrategyConfig(base.realtime)
      : cloneStrategyConfig(DEFAULT_REALTIME_CONFIG);
    mutator(nextRealtime);
    base.realtime = nextRealtime;
    onStrategyConfigChange(base as StrategyConfigDraft);
  };

  const realtime =
    strategyConfig.realtime ?? cloneStrategyConfig(DEFAULT_REALTIME_CONFIG);

  const handleVadChange = (mutator: (draft: VADConfigDraft) => void) => {
    updateRealtime((draft) => {
      const nextVad = draft.vad
        ? cloneStrategyConfig(draft.vad)
        : cloneStrategyConfig(DEFAULT_VAD_CONFIG);
      mutator(nextVad);
      draft.vad = nextVad;
    });
  };

  return (
    <div className="bg-[#f2efe3] p-6 rounded-lg border border-[#e7e2d3] space-y-4">
      <div>
        <h3 className="text-lg font-medium">Realtime Controls</h3>
        <p className="text-sm text-gray-600">
          These settings apply to every strategy and control detection behavior.
        </p>
      </div>

      <div className="bg-white rounded-lg border border-[#e7e2d3] p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-semibold text-[#140d0c]">Turn Detection</h4>
            <p className="text-xs text-gray-500">
              Use smart turn detection to know when the caller stops speaking.
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={realtime.turn_detection_enabled}
              onChange={(e) =>
                updateRealtime((draft) => {
                  draft.turn_detection_enabled = e.target.checked;
                })
              }
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[rgb(255,204,51)]/30 rounded-full peer peer-checked:bg-[rgb(255,204,51)] peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all" />
          </label>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-semibold text-[#140d0c]">Noise Reduction</h4>
            <p className="text-xs text-gray-500">
              Apply DSP noise reduction before streaming audio to the pipeline.
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={realtime.noise_reduction_enabled}
              onChange={(e) =>
                updateRealtime((draft) => {
                  draft.noise_reduction_enabled = e.target.checked;
                })
              }
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[rgb(255,204,51)]/30 rounded-full peer peer-checked:bg-[rgb(255,204,51)] peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all" />
          </label>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-[#e7e2d3] p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-semibold text-[#140d0c]">Voice Activity Detection</h4>
            <p className="text-xs text-gray-500">
              Enable VAD to automatically detect speech start/stop events.
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={Boolean(realtime.vad)}
              onChange={(e) =>
                updateRealtime((draft) => {
                  draft.vad = e.target.checked ? cloneStrategyConfig(DEFAULT_VAD_CONFIG) : null;
                })
              }
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[rgb(255,204,51)]/30 rounded-full peer peer-checked:bg-[rgb(255,204,51)] peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all" />
          </label>
        </div>

        {realtime.vad && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="text-sm">
              <span className="block text-gray-600 mb-1">Confidence</span>
              <input
                type="number"
                min={0}
                max={1}
                step={0.05}
                value={realtime.vad.confidence}
                onChange={(e) =>
                  handleVadChange((draft) => {
                    draft.confidence = Number(e.target.value);
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </label>
            <label className="text-sm">
              <span className="block text-gray-600 mb-1">Start secs</span>
              <input
                type="number"
                min={0.05}
                step={0.05}
                value={realtime.vad.start_secs}
                onChange={(e) =>
                  handleVadChange((draft) => {
                    draft.start_secs = Number(e.target.value);
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </label>
            <label className="text-sm">
              <span className="block text-gray-600 mb-1">Stop secs</span>
              <input
                type="number"
                min={0.2}
                step={0.05}
                value={realtime.vad.stop_secs}
                onChange={(e) =>
                  handleVadChange((draft) => {
                    draft.stop_secs = Number(e.target.value);
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </label>
            <label className="text-sm">
              <span className="block text-gray-600 mb-1">Min volume</span>
              <input
                type="number"
                min={0}
                max={1}
                step={0.05}
                value={realtime.vad.min_volume}
                onChange={(e) =>
                  handleVadChange((draft) => {
                    draft.min_volume = Number(e.target.value);
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </label>
          </div>
        )}
      </div>
    </div>
  );
};

export default RealtimeControlsCard;
